/**
 * React Query hooks pour les réunions (Meetings API)
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { meetingsApi, Meeting, CreateMeetingDto, UpdateMeetingDto, FilterMeetingDto } from '@/services/api/meetings.api';

export function useMeetings(
  filters?: FilterMeetingDto,
  options?: Omit<UseQueryOptions<Meeting[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Meeting[], Error>({
    queryKey: ['meetings', filters],
    queryFn: () => meetingsApi.list(filters),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useMeeting(
  id: string | undefined,
  options?: Omit<UseQueryOptions<Meeting, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Meeting, Error>({
    queryKey: ['meetings', id],
    queryFn: () => {
      if (!id) throw new Error('Meeting ID is required');
      return meetingsApi.getById(id);
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useCreateMeeting(
  options?: Omit<UseMutationOptions<Meeting, Error, CreateMeetingDto>, 'mutationFn'>
) {
  const queryClient = useQueryClient();
  return useMutation<Meeting, Error, CreateMeetingDto>({
    mutationFn: (data) => meetingsApi.create(data),
    onSuccess: (newMeeting) => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.setQueryData(['meetings', newMeeting.id], newMeeting);
    },
    ...options,
  });
}

export function useUpdateMeeting(
  options?: Omit<UseMutationOptions<Meeting, Error, { id: string; data: UpdateMeetingDto }>, 'mutationFn'>
) {
  const queryClient = useQueryClient();
  return useMutation<Meeting, Error, { id: string; data: UpdateMeetingDto }>({
    mutationFn: ({ id, data }) => meetingsApi.update(id, data),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.setQueryData(['meetings', updated.id], updated);
    },
    ...options,
  });
}

export function useCancelMeeting(
  options?: Omit<UseMutationOptions<Meeting, Error, { id: string; reason?: string }>, 'mutationFn'>
) {
  const queryClient = useQueryClient();
  return useMutation<Meeting, Error, { id: string; reason?: string }>({
    mutationFn: ({ id, reason }) => meetingsApi.cancel(id, reason),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.setQueryData(['meetings', updated.id], updated);
    },
    ...options,
  });
}
